import React from "react";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

const COLLAPSED_HEIGHT = 260;
const COLLAPSED_LINES = 4;

const sxRoot = (expanded) => ({
  maxWidth: 800,
  width: { xs: "95%", sm: "80%", md: "60%" },
  mx: "auto",
  backgroundColor: "#0c6170",
  height: expanded ? "auto" : COLLAPSED_HEIGHT,
  overflow: "hidden",
  transition: "height 0.35s ease",
  position: "relative",
});

const sxMedia = {
  height: 60,
  width: 60,
  borderRadius: 50,
  mx: "auto",
  mt: 2,
  display: "block",
};

export default function OneQuote({ item }) {
  const cardRef = React.useRef(null);
  const textRef = React.useRef(null);

  const [expanded, setExpanded] = React.useState(false);
  const [isOverflowing, setIsOverflowing] = React.useState(false);

  // Detect overflow
  React.useEffect(() => {
    if (textRef.current && !expanded) {
      setIsOverflowing(
        textRef.current.scrollHeight > textRef.current.clientHeight
      );
    }
  }, [item, expanded]);

  // Reset when quote changes
  React.useEffect(() => {
    setExpanded(false);
  }, [item]);

  // Auto-scroll on expand
  React.useEffect(() => {
    if (expanded && cardRef.current) {
      cardRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [expanded]);

  if (!item) return null;

  return (
    <Card ref={cardRef} sx={sxRoot(expanded)}>
      <CardActionArea disableRipple>
        <CardMedia sx={sxMedia} image={item.image} />

        <CardContent sx={{ textAlign: "center" }}>
          <Typography
            ref={textRef}
            sx={{
              color: "#dbf5f0",
              fontSize: 20,
              fontFamily: "ui-monospace",
              display: "-webkit-box",
              WebkitLineClamp: expanded ? "unset" : COLLAPSED_LINES,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              transition: "all 0.3s ease",
            }}
          >
            “ {item.quote} ”
          </Typography>

          {/* Fade hint */}
          {!expanded && isOverflowing && (
            <Box
              sx={{
                position: "absolute",
                bottom:50,
                left: 0,
                right: 0,
                height: 50,
                background:
                  "linear-gradient(to bottom, rgba(12,97,112,0), #0c6170)",
                pointerEvents: "none",
              }}
            />
          )}

          {/* Read more / Show less: moved to CardActions to avoid nested button inside CardActionArea */}
        </CardContent>
      </CardActionArea>

      <CardActions sx={{ justifyContent: "center", pb: 2, gap: 2 }}>
        {isOverflowing && (
          <Button
            size="small"
            sx={{
              color: "#7EC8E3",
              textTransform: "none",
            }}
            onClick={() => setExpanded(!expanded)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setExpanded(!expanded);
              }
            }}
          >
            {expanded ? "Show less" : "Read more"}
          </Button>
        )}
        <Typography sx={{ color: "#dbf5f0", fontFamily: "ui-monospace" }}>
          — {item.name}
        </Typography>
      </CardActions>
    </Card>
  );
}
